
    import __i18nConfig from '@next-translate-root/i18n'
    import __loadNamespaces from 'next-translate/loadNamespaces'
    import React, { useState, useEffect, useRef } from 'react';
import Head from 'next/head';
import useTranslation from 'next-translate/useTranslation';
import { addDays, isWithinInterval, parseISO } from 'date-fns';
import { getFlightData } from '../lib/flightsFetcher';
import PageFlightTable from '../components/PageFlightTable';
import RelatedLinks from '../components/RelatedLinks';
// How long to stop asking /api/flights after it fails to reach CPH. Long
// enough that a sustained block is not re-probed every minute by every open
// tab, short enough that a transient failure heals without a reload.
const SERVER_RETRY_BACKOFF_MS = 10 * 60 * 1000;
async function getStaticProps() {
    try {
        const now = new Date();
        const to = addDays(now, 1);
        const flights = (await getFlightData({ direction: 'D' })).filter((flight) => isWithinInterval(parseISO(flight.ExpectedDateTime), {
            start: now,
            end: to
        }));
        return {
            props: {
                initialFlights: flights,
                initialFrom: now.toISOString(),
                initialTo: to.toISOString()
            },
            revalidate: 60
        };
    }
    catch (error) {
        console.error('Error building departures page:', error);
        return {
            props: { initialFlights: null, initialFrom: null, initialTo: null },
            revalidate: 60
        };
    }
}
function Departures({ initialFlights, initialFrom, initialTo }) {
    const { t } = useTranslation('departures');
    const [flightsData, setFlightsData] = useState(initialFlights);
    const [from, setFrom] = useState(initialFrom);
    const [to, setTo] = useState(initialTo);
    // Back off from a failing /api/flights instead of abandoning it: the route
    // retries CPH three times before giving up, so re-asking it every minute
    // costs an invocation and seconds of latency to learn what we already
    // know. It expires rather than latching, because a cold start or a one-off
    // blip is not the sustained block this guards against, and latching would
    // put every visitor on the direct path this change exists to avoid.
    const serverRetryAfter = useRef(0);
    const fetchFlightData = async () => {
        // Prefer our own API. It fetches CPH once and caches, so one request
        // serves every visitor. Going straight to CPH from each browser works
        // (see the fallback below) but multiplies load on them by the number
        // of people on the page, which is not a good way to stay welcome.
        if (Date.now() >= serverRetryAfter.current) {
            try {
                const response = await fetch('/api/flights?d=D');
                const result = await response.json();
                if (result.flights) {
                    setFlightsData(result.flights);
                    setFrom(result.from);
                    setTo(result.to);
                    return;
                }
                // 200 with { error }: the server reached us but not CPH.
                serverRetryAfter.current = Date.now() + SERVER_RETRY_BACKOFF_MS;
            }
            catch {
                serverRetryAfter.current = Date.now() + SERVER_RETRY_BACKOFF_MS;
            }
        }
        // Fallback: fetch CPH from the visitor's browser. Akamai refuses
        // browser-shaped agents from datacenter IPs, so the server can lose
        // access while real visitors keep theirs. CPH sends
        // Access-Control-Allow-Origin: *, and next.config.js allows the host
        // in connect-src, so this is a plain cross-origin GET.
        try {
            const now = new Date();
            const from = now;
            const to = addDays(now, 1);
            const flights = (await getFlightData({ direction: 'D' })).filter((flight) => isWithinInterval(parseISO(flight.ExpectedDateTime), {
                start: from,
                end: to
            }));
            setFlightsData(flights);
            setFrom(from.toISOString());
            setTo(to.toISOString());
        }
        catch {
            // keep last good data
        }
    };
    useEffect(() => {
        fetchFlightData();
        const intervalId = setInterval(() => {
            if (document.visibilityState === 'visible')
                fetchFlightData();
        }, 1000 * 60);
        return () => clearInterval(intervalId);
    }, []);
    const faqs = [1, 2, 3].map((i) => ({
        q: t(`faq_q${i}`),
        a: t(`faq_a${i}`)
    }));
    const faqJsonLd = {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: faqs.map(({ q, a }) => ({
            '@type': 'Question',
            name: q,
            acceptedAnswer: { '@type': 'Answer', text: a }
        }))
    };
    return (<>
            <Head>
                <title>{t('meta_title')}</title>
                <meta name="description" content={t('meta_description')}/>
                <meta name="keywords" content={t('meta_keywords')}/>
                <meta name="robots" content="all"/>
                <meta property="og:title" content={t('meta_title')}/>
                <meta property="og:description" content={t('meta_description')}/>
                <script type="application/ld+json" dangerouslySetInnerHTML={{
            __html: JSON.stringify(faqJsonLd)
        }}/>
            </Head>

            <div className="bg-white p-4 m-4 sm:rounded text-gray-800">
                <h1 className="text-2xl sm:text-3xl font-bold">
                    {t('page_title')}
                </h1>
                <p className="mt-2 text-gray-600">{t('intro')}</p>

                {!flightsData ? (
        // getStaticProps already ran, so null means the CPH fetch
        // failed, not that data is on its way. A spinner here spins
        // forever. The 60s client interval swaps in the table if
        // CPH recovers.
        <p className="mt-4 text-gray-600">
                        {t('common:flights_unavailable')}
                    </p>) : (<PageFlightTable flights={flightsData} from={new Date(from)} to={new Date(to)} title={'departures:table_title'} reminderBell/>)}

                <section className="mt-8">
                    <h2 className="text-xl font-semibold">
                        {t('faq_heading')}
                    </h2>
                    <dl className="mt-2 divide-y divide-gray-200">
                        {faqs.map(({ q, a }, i) => (<div key={i} className="py-3">
                                <dt className="font-semibold">{q}</dt>
                                <dd className="mt-1 text-gray-700">{a}</dd>
                            </div>))}
                    </dl>
                </section>
            </div>

            <RelatedLinks links={[
            { href: '/', labelKey: 'security_wait_times' },
            { href: '/arrivals', labelKey: 'arrivals' },
            {
                href: '/blog/how-early-to-arrive-at-cph',
                labelKey: 'link_how_early'
            },
            { href: '/compensation', labelKey: 'link_compensation' }
        ]}/>
        </>);
}
export default Departures;

    async function __Next_Translate__getStaticProps__1a1723ec67e__(ctx) {
      const res = await getStaticProps(ctx)
      return {
        
        ...res,
        props: {
          ...(res.props || {}),
          ...(await __loadNamespaces({
            ...ctx,
            ...__i18nConfig,
            pathname: '/departures',
            loaderName: 'getStaticProps',
            loadLocaleFrom: __i18nConfig && __i18nConfig.loadLocaleFrom || ((l, n) => import(`@next-translate-root/locales/${l}/${n}`).then(m => m.default)),
          }))
        }
      }
    }
    export { __Next_Translate__getStaticProps__1a1723ec67e__ as getStaticProps }
  